import React, { createContext, useContext } from 'react';
import { HolderOutlined } from '@ant-design/icons';
import { useDraggable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

// Threads SortableItem's drag listeners into any DragHandle rendered inside the card body
export const DragHandleContext = createContext(null);

export function DragHandle() {
    const listeners = useContext(DragHandleContext);
    if (!listeners) return null;
    return (
        <span
            className="sidebar-drag-handle"
            {...listeners}
            onPointerDown={(e) => {
                e.stopPropagation();
                listeners.onPointerDown?.(e);
            }}
        >
            <HolderOutlined />
        </span>
    );
}

// Sortable wrapper; drag listeners live on DragHandle inside the card, not on this element
export function SortableItem({ id, children, data, groupZoneId }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
    };
    return (
        <DragHandleContext.Provider value={listeners}>
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                className="sortable-item-container"
                {...(groupZoneId != null ? { 'data-group-zone-id': String(groupZoneId) } : {})}
            >
                {children}
            </div>
        </DragHandleContext.Provider>
    );
}

// Exposes data-drag-card-id for pointermove VM-drag detection; drop registration handled by parent SortableItem
export function DroppableCard({ id, children, isOver }) {
    return (
        <div
            data-drag-card-id={String(id)}
            className={classNames('droppable-card-wrapper', { 'droppable-card--active': isOver })}
        >
            {children}
        </div>
    );
}

// Draggable wrapper for nested app cards; stops pointerDown bubbling to prevent parent SortableItem from activating
export function DraggableNestedApp({ appId, groupId, sourceName, children }) {
    const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
        id: `nested-app-drag-${appId}`,
        data: { type: 'application', sourceId: appId, sourceName, sourceGroupId: groupId },
    });
    return (
        <div
            ref={setNodeRef}
            style={{ opacity: isDragging ? 0 : 1, cursor: isDragging ? 'grabbing' : 'grab' }}
            className="nested-app-drag-wrapper"
            {...attributes}
            {...listeners}
            onPointerDown={(e) => {
                e.stopPropagation();
                listeners?.onPointerDown?.(e);
            }}
        >
            {children}
        </div>
    );
}

// Draggable wrapper for app-section cards; separate from SortableItem's handle-only reorder drag
export function DraggableAppCard({ appId, sourceName, children }) {
    const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
        id: `app-section-assign-${appId}`,
        data: { type: 'application', sourceId: appId, sourceName },
    });
    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            style={{ opacity: isDragging ? 0 : 1, cursor: isDragging ? 'grabbing' : 'grab' }}
        >
            {children}
        </div>
    );
}
